import { zodResolver } from "@hookform/resolvers/zod";
import React, { forwardRef, useCallback, useEffect, useId } from "react";
import {
  type DefaultValues,
  FormProvider,
  useForm,
  type UseFormReturn,
} from "react-hook-form";
import { z, type ZodObject } from "zod";
import { Button } from "../Button/Button.js";
import { ButtonGroup } from "../Button/ButtonGroup.js";
import { cn } from "../classnames.js";
import AlertSmall from "../vendor/AlertSmall/AlertSmall.js";

const OnSubmitErrorSchema = z.object({
  message: z.string().trim().min(1),
});

export type FormProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Schema extends ZodObject<any>,
> = {
  /**
   * The text contents of the cancel button.
   *
   * @default Cancel
   */
  cancelButtonLabel?: string | undefined;

  /**
   * Content to render within the element.
   *
   * In advanced use cases you may specify a function that will be invoked to render your children.
   * The render function receives an object that includes the internal form context object returned
   * by `react-hook-form#useForm()`. Note that this functionality is advanced and not unstable, and
   * it may change at any time.
   *
   * @experimental Passing a render function as children is unstable functionality.
   */
  children?:
    | React.ReactNode
    | ((props: { context: UseFormReturn<z.infer<Schema>> }) => React.ReactNode)
    | undefined;

  /**
   * A class property to attach to the element.
   *
   * @see {@link !Element.className}
   */
  className?: string | undefined;

  /**
   * A list of default class names that are applied to the form element. By default, the form is a
   * flexbox with consistent gapping applied between each of its elements; if you need to disable
   * this behavior, you can set this to `false`.
   *
   * @see {@link !Element.className}
   */
  defaultClassName?: string | string[] | false | undefined;

  /**
   * A set of initial values used to pre-fill the form.
   */
  defaultValues?: DefaultValues<z.infer<Schema>> | undefined;

  /**
   * A callback function invoked when a user clicks the "Save" button.
   *
   * The submit button is not displayed by default; you must pass the `onSubmit` props for it to
   * appear.
   */
  onSubmit?: ((data: z.infer<Schema>) => Promise<void> | void) | undefined;

  /**
   * A callback function invoked when a user clicks the "Cancel" button.
   *
   * The cancel button is not displayed by default; you must pass the `onCancel` prop for it to
   * appear.
   */
  onCancel?: (({ reset }: { reset: () => void }) => void) | undefined;

  /**
   * Whether or not the form should be disabled. When disabled, all form field children and any
   * interactive elements (e.g. buttons) are disabled.
   *
   * @default false
   */
  disabled?: boolean | undefined;

  /**
   * By default, the form will automatically determine whether or not it's in a loading state. For
   * example: The form will automatically enter a loading state during submission.
   *
   * You can override this behavior by explicitly specifying the `loading` prop.
   */
  loading?: boolean | undefined;

  /**
   * A Zod schema used to validate the form.
   */
  schema?: Schema | undefined;

  /**
   * The text contents of the submit button.
   *
   * @default Save
   */
  submitButtonLabel?: string | undefined;

  /**
   * An optional title for the form
   */
  title?: string | undefined;
};

/**
 * A `Form` collects information from a user.
 *
 * ![Form](./screenshots/form.png)
 *
 * @param props
 * @see {@link Checkbox}
 * @see {@link FormField}
 * @see {@link FormFieldSecret}
 * @see {@link Select}
 * @example
 * ```tsx
 * import { Form, FormField, FormFieldSecret } from "@netlify/sdk/ui/react/components";
 *
 * <Form onSubmit={(fields) => console.log("Form submitted.", { fields })}>
 *   <FormField name="name" label="Your Name" required />
 *   <FormFieldSecret
 *     name="deepest_secret"
 *     label="Your Deepest Secret"
 *     required
 *   />
 *   <FormField name="explanation" label="An Explanation" type="textarea" />
 *   <FormField type="number" name="id_number" label="Your Serial Number" />
 * </Form>
 * ```
 */
export const Form = forwardRef(function Form<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Schema extends ZodObject<any>,
>(
  {
    cancelButtonLabel = "Cancel",
    children,
    className,
    defaultClassName = "tw-flex tw-flex-col tw-gap-6",
    defaultValues,
    disabled = false,
    loading,
    onCancel: originalOnCancel,
    onSubmit: originalOnSubmit,
    schema,
    submitButtonLabel = "Save",
    title,
  }: FormProps<Schema>,
  ref: React.ForwardedRef<HTMLFormElement>,
) {
  const formId = useId();

  const methods = useForm({
    defaultValues: defaultValues!,
    disabled: disabled || loading,
    resolver: schema !== undefined ? zodResolver(schema) : undefined,
  });
  const { clearErrors, control, formState, handleSubmit, setError, watch } =
    methods;

  // XXX(ndhoule): This fixes an issue where when the form is disabled the enabled again, the form's
  // `formState.disabled` state remains `false`. I'm sure there's an issue somewhere in the RHF repo
  // for this, but I haven't looked too hard yet.
  useEffect(() => {
    // Technically, we only need to call this in the event the form goes from disabled -> enabled.
    // It's more straightforward to call it whenever the disabled state changes, though, and doesn't
    // have any negative effect.
    control._disableForm(disabled);
  }, [control, disabled]);

  useEffect(() => {
    // When a user interacts with the form after a form-level error is set, clear the error.
    const { unsubscribe } = watch((_data, { type }) => {
      if (type === "change") {
        clearErrors("root.onSubmit");
      }
    });
    return unsubscribe;
  }, [clearErrors, watch]);

  const onCancel = useCallback(() => {
    originalOnCancel?.({ reset: methods.reset });
  }, [originalOnCancel, methods.reset]);

  const onSubmit = useCallback(
    async (data: z.infer<Schema>) => {
      try {
        await originalOnSubmit?.(data);
      } catch (err) {
        let message = "An unexpected error occurred while saving the form.";
        const errParseResult = OnSubmitErrorSchema.safeParse(err);
        if (errParseResult.success) {
          message = errParseResult.data.message;
        }

        setError("root.onSubmit", { message });
      }
    },
    [originalOnSubmit, setError],
  );

  const isFormLoading =
    loading ?? (formState.isLoading || formState.isSubmitting);

  return (
    <FormProvider {...methods}>
      <form
        title={title}
        aria-errormessage={`form-${formId}--root-error`}
        aria-invalid={formState.errors.root?.["onSubmit"] !== undefined}
        className={cn(defaultClassName, className)}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(onSubmit)}
        ref={ref}
      >
        {typeof children === "function"
          ? children({ context: methods })
          : children}

        {/* Show a form-level error. This captures errors at the form level, e.g. uncaught errors
            within onSubmit. Once an error is set, typing in the form or resubmitting will clear the
            error. */}
        {formState.errors.root?.["onSubmit"]?.message !== undefined && (
          <AlertSmall
            id={`form-${formId}--root-error`}
            error={formState.errors.root["onSubmit"].message}
          />
        )}

        <ButtonGroup>
          {typeof onSubmit === "function" && (
            <Button
              disabled={formState.disabled || formState.isValidating}
              loading={isFormLoading}
              type="submit"
            >
              {submitButtonLabel}
            </Button>
          )}
          {typeof originalOnCancel === "function" && (
            <Button
              disabled={
                formState.disabled || isFormLoading || formState.isValidating
              }
              level="secondary"
              onClick={onCancel}
            >
              {cancelButtonLabel}
            </Button>
          )}
        </ButtonGroup>
      </form>
    </FormProvider>
  );
}) as <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Schema extends ZodObject<any>,
>(
  props: FormProps<Schema> & {
    ref?: React.ForwardedRef<HTMLFormElement> | undefined;
  },
) => JSX.Element;
